'use client';

import { Flex } from '@ironhack/design-system2/components/flex';
import { backgroundToTokens } from '@ironhack/design-system2/utils';
import { usePathname } from 'next/navigation';
import { type ReactElement, useEffect, useState } from 'react';

import { NotFoundPageHeader } from '@/app/components/server/not-found-page-header';
import { type PageData, type PageProps } from '@/app/not-found';
import { type BasePageParams, splitUrlRegion } from '@/app/urls/url-mapping';
import { urlToDatoLocale } from '@/lib/utils';
import { type UrlRegion, isLanguage, isRegion } from '@/types';

type PathParams = Pick<BasePageParams, 'language' | 'region'>;

type NotFoundPage = PageProps['allNotFoundPages'][number];

const getPathParams = (path: string): PathParams => {
  const [, urlRegion] = path.split('/');
  let { region, language } = splitUrlRegion(urlRegion as UrlRegion);

  if (!isRegion(region)) {
    region = 'es';
  }
  if (!isLanguage(language)) {
    language = 'en';
  }

  return {
    region,
    language,
  };
};

const getPageData = (pathParams: PathParams, data: PageProps): PageData => {
  const { region, language } = pathParams;

  const notFoundPage = data.allNotFoundPages.find(
    (page) => page.region.code === region
  ) as NotFoundPage;

  let pageLocale = notFoundPage._allHeaderLocales.find(
    (header) => header.locale === urlToDatoLocale[`${region}${language}`]
  );

  if (!pageLocale) {
    pageLocale = notFoundPage._allHeaderLocales.find(
      (header) => header.locale === urlToDatoLocale[`${region}en`]
    ) as NotFoundPage['_allHeaderLocales'][number];
  }

  return pageLocale.value[0];
};

export const NotFound = (props: PageProps): ReactElement => {
  const { allNotFoundPages } = props;
  const path = usePathname() ?? '';
  const params = getPathParams(path);
  const [data, setData] = useState<PageData>({
    title: '',
    description: '',
    buttonText: '',
  });

  const { bg } = backgroundToTokens(
    allNotFoundPages[0].headerBackground || 'brand'
  );

  useEffect(() => {
    const pathParams = getPathParams(path);
    const pageData = getPageData(pathParams, props);
    setData(pageData);
  }, [props, path]);

  return (
    <Flex bg={bg} direction="column" h="100vh" key="not-found">
      <NotFoundPageHeader data={data} my="auto" params={params} />
    </Flex>
  );
};
